import React, { useState, useEffect } from "react";
import "./styles.css";
import TopHeader from "./nav_top.js";
import SevenDayPLTiles from "./_1_7_day_week_PL_tiles_v3.js";

import NoteGroup from "./_57_note_group.js";
import MostPopularTimes from "./_10_most_popular_times.js";
import BySymbolTablePLTYD from "./_59_by_symbol_PL_YTD.js";
import CalendarYear from "./_12_calendar_year.js";
import MaxIntradayTrendingChart from "./_48_max_intraday_trending_table.js";
import MultiDayTradesTable from "./_61_multi_day_trades.js";
import { DateFilterProvider, useDateFilter } from './_40b_date_filter_context.js';
import TodayTradeTable from "./_4_today_trade_table.js";

const MODULE_NAME = "Detail - today_pg2";

// Responsive hook
function useIsMobile(breakpoint = 700) {
    const [isMobile, setIsMobile] = React.useState(window.matchMedia(`(max-width: ${breakpoint}px)`).matches);
    React.useEffect(() => {
        const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
        const handler = (e) => setIsMobile(e.matches);
        mediaQuery.addEventListener("change", handler);
        return () => mediaQuery.removeEventListener("change", handler);
    }, [breakpoint]);
    return isMobile;
}

// Layout helpers
function GradientSeparator() {
    // Check if module_name is in URL - if so, don't show gradient
    const hasModuleName = React.useMemo(() => {
        if (typeof window === 'undefined') return false;
        const params = new URLSearchParams(window.location.search);
        return !!params.get('module_name');
    }, []);

    // Return null if module_name is present
    if (hasModuleName) return null;

    return (
        <div
            style={{
                height: 8,
                width: "100%",
                background: "linear-gradient(90deg, #0a2342 0%, #CCC 100%)",
                margin: "24px 0"
            }}
        />
    );
}

// Inner component that uses DateFilter context
function DetailsContent({ userId, effectiveDate, screen, refreshKey, isMobile, setScreen }) {
    const { navigationDate } = useDateFilter();

    // Use navigationDate if available (from filter), otherwise fall back to effectiveDate
    const activeDate = (navigationDate && /^\d{4}-\d{2}-\d{2}$/.test(navigationDate)) ? navigationDate : effectiveDate;

    const renderDailyLogSection = () => {
        return (
            <NoteGroup
                userId={userId}
                activeDate={activeDate}
                screen={screen}
                refreshKey={refreshKey}
            />
        );
    };

    return (
        <>
            <SevenDayPLTiles
                userId={userId}
                activeDate={activeDate}
                screen={screen}
                refreshKey={refreshKey}
            />

            <GradientSeparator />

            {isMobile ? (
                <div className="mobile-stack" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {renderDailyLogSection()}
                    <div style={{ width: "100%" }}></div>
                    <div style={{ width: "100%" }}>
                        <TodayTradeTable userId={userId} activeDate={activeDate} screen={screen} refreshKey={refreshKey} />
                    </div>
                </div>
            ) : (
                <div style={{ display: "flex", gap: 20 }}>
                        <div style={{ flex: 1, minWidth: 550 }}> 

                        <NoteGroup userId={userId} activeDate={activeDate} screen={screen} refreshKey={refreshKey} />
                        <BySymbolTablePLTYD userId={userId} activeDate={activeDate} screen={screen} refreshKey={refreshKey} />
                        <CalendarYear userId={userId} activeDate={activeDate} screen={screen} refreshKey={refreshKey} />
                    </div>
                    <div style={{ flex: 3, minWidth: 500, minHeight: 600, display: "flex", flexDirection: "column" }}>
                        <MaxIntradayTrendingChart userId={userId} activeDate={activeDate} screen={screen} refreshKey={refreshKey} />
                        <TodayTradeTable userId={userId} activeDate={activeDate} screen={screen} refreshKey={refreshKey} />

                    </div>
                </div>
            )}

            <GradientSeparator />
            <MostPopularTimes userId={userId} activeDate={activeDate} screen={screen} refreshKey={refreshKey} />
            <GradientSeparator />
        </>
    );
}

export default function TodayDetailsPage({ date, refreshKey, screen, setScreen }) {
    const [isLoading, setIsLoading] = useState(true);

    // Priority: date prop > navigationDate in sessionStorage > latest_date in sessionStorage > today
    let effectiveDate = date || sessionStorage.getItem("navigationDate") || sessionStorage.getItem("latest_date");

    if (!effectiveDate) {
        effectiveDate = new Date().toISOString().slice(0, 10);
    }

    // Clean up query string once component mounts
    useEffect(() => {
        const url = new URL(window.location.href);
        const params = url.searchParams;

        let changed = false;
        // List parameters that should be cleaned up ONLY after they've been used
        const toDelete = ["date", "symbol", "qty", "demo", "screen"];

        toDelete.forEach(p => {
            if (params.has(p)) {
                params.delete(p);
                changed = true;
            }
        });

        if (changed) {
            // Update browser history without reloading to provide a clean URL
            const newUrl = params.toString() ? `${url.pathname}?${params.toString()}` : url.pathname;
            window.history.replaceState({}, "", newUrl);
        }

        setIsLoading(false);
    }, []);

    // User ID logic
    const sessionUserId = sessionStorage.getItem("user_id") ? Number(sessionStorage.getItem("user_id")) : null;
    const userId = sessionUserId;

    const isMobile = useIsMobile();

    // Show loading while cleaning up URL
    if (isLoading) {
        return <div style={{ color: "#fff", padding: 32 }}>Loading...</div>;
    }

    return (
        <DateFilterProvider>
            <div className="main-dash-container" style={{ maxWidth: 3000, margin: "0 auto", padding: "0 0 0 0" }} data-analytics={MODULE_NAME}>
                <TopHeader
                    userId={userId}
                    screen={screen}
                    refreshKey={refreshKey}
                    onNavigate={setScreen}
                />

                <DetailsContent
                    userId={userId}
                    effectiveDate={effectiveDate}
                    screen={screen}
                    refreshKey={refreshKey}
                    isMobile={isMobile}
                    setScreen={setScreen}
                />
                <MultiDayTradesTable userId={userId} />
            </div>
        </DateFilterProvider>
    );
}